import { useEffect, useState } from "react";
import { ConfirmDialog } from "../../../components/shared";
import {
  OwcButton,
  OwcCheckbox,
  OwcListItem,
  OwcSelectDropdown,
  OwcTypography,
} from "@one/react";
import { useFormikContext } from "formik";
import { getAllData } from "../../../utils/helpers/fetching";
import {
  LIST_USERS_FILTERS,
  LIST_BOOKING_GROUP_CONFIGURATION_BY_CREATE_DATE,
  LIST_USERS_DISPLAYS,
} from "../../../gql/bookingapi";
import { useSelector } from "react-redux";

const normalizeSelectionState = (values) => ({
  filterName: values?.filter?.filterName || "",
  columnSetName: values?.columnSet?.displayName || "",
  groups: Array.isArray(values?.groups)
    ? [...values.groups].sort((a, b) => String(a).localeCompare(String(b), "pl", { sensitivity: "base" }))
    : [],
});

const GroupDialog = ({
  open,
  onCancel,
  submit,
  loading,
  isEditMode,
  client,
}) => {
  const formik = useFormikContext();
  const [groupOptions, setGroupOptions] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]);
  const [columnSetOptions, setColumnSetOptions] = useState([]);
  const storeUser = useSelector((theStore) => theStore.user);


  useEffect(() => {
    const fetchGroups = async () => {
      const siteFilter = storeUser?.site
        ? { or: [{ siteName: { eq: storeUser.site } }, { siteName: { attributeExists: false } }] }
        : undefined;
      const { items } = await getAllData({
        client,
        query: LIST_BOOKING_GROUP_CONFIGURATION_BY_CREATE_DATE,
        variables: siteFilter ? { filter: siteFilter } : {},
        fetchPolicy: "network-only",
        dataPath: ["data", "listBookingGroupConfigurationByCreatedDate"],
        drillData: false,
      });
      items.sort((a, b) =>
        a.groupName.localeCompare(b.groupName, "pl", { sensitivity: "base" })
      );
      const options = items.map((item) => ({
        label: item.groupName,
        value: item.groupName,
      }));

      setGroupOptions(options);
    };

    if (open) fetchGroups();  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client]);

  const fetchFilters = async () => {
    const response = await client.query({
      query: LIST_USERS_FILTERS,
      variables: { limit: 100, siteName: storeUser.site },
      fetchPolicy: "network-only",
    });

    const items = response?.data?.listAllUsersFilters ?? [];
    let availableFilters = items.filter((item) => {
      const isUnshared = !item.sharedWithGroups || item.sharedWithGroups.length === 0;
      const isCurrent =
        isEditMode &&
        formik.values.filter &&
        item.filterName === formik.values.filter.filterName

      return isUnshared || isCurrent;
    });

    availableFilters.sort((a, b) =>
      a.filterName.localeCompare(b.filterName, 'pl', { sensitivity: "base" })
    );
    const options = availableFilters.map((item) => ({
      label: item.filterName,
      value: item,
    }));
    setFilterOptions(options);
  };

  const fetchColumnSets = async () => {
    const response = await client.query({
      query: LIST_USERS_DISPLAYS,
      variables: { limit: 100, siteName: storeUser.site },
      fetchPolicy: "network-only",
    });

    const items = response?.data?.listAllUsersDisplays ?? [];

    // Column sets are intentionally reusable across multiple group views.
    // Keep all named column sets selectable (shared and unshared).
    const availableColumns = items.filter((item) => Boolean(item?.displayName));

    availableColumns.sort((a, b) =>
      a.displayName.localeCompare(b.displayName, "pl", { sensitivity: "base" }),
    );

    const options = availableColumns.map((item) => ({
      label: item.displayName,
      value: item,
    }));

    setColumnSetOptions(options);
  };

  useEffect(() => {
    if (
      open &&
      (!isEditMode || (
        formik.values.filter && formik.values.filter.filterName &&
        formik.values.columnSet && formik.values.columnSet.displayName
      ))
    ) {
      fetchFilters();
      fetchColumnSets();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditMode, formik.values.filter, formik.values.columnSet]);

  const isFilterValid = !!formik.values.filter;
  const isColumnValid = !!formik.values.columnSet;
  const isGroupsValid = Array.isArray(formik.values.groups) && formik.values.groups.length > 0;
  const isFormValid = (isFilterValid || isColumnValid) && isGroupsValid;
  const initialSelectionState = normalizeSelectionState(formik.initialValues);
  const currentSelectionState = normalizeSelectionState(formik.values);
  const hasChanges = !isEditMode || JSON.stringify(initialSelectionState) !== JSON.stringify(currentSelectionState);

  return (
    <ConfirmDialog
      open={open}
      close={onCancel}
      approveText={isEditMode ? "Confirm" : "Create"}
      approveColor="primary"
      approveVariant="contained"
      cancelText="Cancel"
      cancelVariant="outlined"
      cancelColor="primary"
      onCancel={onCancel}
      isDivider
      size="md"
      testid={"assign-filter-group-dialog"}
      title={isEditMode ? "Edit Group Assignment" : "Assign Group"}
      renderActions={({ cancelText, approveText, close }) => (
        <div style={{ paddingBottom: 8, display: "flex", marginLeft: "auto" }}>
          <OwcButton
            data-testid="confirm-dialog-actions-button-cancel"
            onClick={() => {
              close();
              if (onCancel) {
                onCancel();
              }
            }}
            variant="secondary"
            style={{ marginRight: 32 }}
          >
            {cancelText}
          </OwcButton>
          <OwcButton
            data-testid="confirm-dialog-actions-button-approve"
            onClick={() => {
              if (loading || !isFormValid || !hasChanges) {
                return;
              }
              if (submit) {
                submit();
              }
            }}
            variant="primary"
            disabled={loading || !isFormValid || !hasChanges}
          >
            {approveText}
          </OwcButton>
        </div>
      )}
    >
      <OwcTypography>
        Select a filter and assign it to one or more groups. Users in those
        groups will be able to apply the filter in their views.
      </OwcTypography>

      <div
        style={{
          display: "flex",
          gap: "24px",
          flexWrap: "wrap",
          alignItems: "flex-start",
          marginTop: 24,
        }}
      >
        {/* Dropdown filter */}
        <div style={{ flex: "1 1 220px", minWidth: 220, maxWidth: 320 }}>
          <OwcSelectDropdown
            label="Select filter *"
            name="select-filter"
            dataTestid="filter-select-dropdown"
            style={{ width: "100%" }}
            onSelectChange={(e) => {
              const idx = e.detail[0];
              formik.setFieldValue("filter", filterOptions[idx]?.value);
              formik.setFieldTouched("filter", true);
            }}
            required
          >
            {filterOptions.map((option, idx) => {
              const isSelected =
                formik.values.filter?.filterName === option.value?.filterName &&
                formik.values.filter?.userID === option.value?.userID;
              return (
                <OwcListItem
                  key={`${option.value?.id}-${idx}`}
                  value={option.value}
                  selected={isSelected}
                >
                  {option.label}
                </OwcListItem>
              );
            })}
          </OwcSelectDropdown>
        </div>

        {/* Dropdown column set */}
        <div style={{ flex: "1 1 220px", minWidth: 220, maxWidth: 320 }}>
          <OwcSelectDropdown
            label="Select column set"
            name="select-column-set"
            dataTestid="column-set-select-dropdown"
            style={{ width: "100%" }}
            onSelectChange={(e) => {
              const idx = e.detail[0];
              formik.setFieldValue("columnSet", columnSetOptions[idx]?.value);
              formik.setFieldTouched("columnSet", true);
            }}
          >
            {columnSetOptions.map((option, idx) => {
              const isSelected =
                formik.values.columnSet?.displayName === option.value?.displayName
              return (
                <OwcListItem
                  key={`${option.value?.id}-${idx}`}
                  value={option.value}
                  selected={isSelected}
                >
                  {option.label}
                </OwcListItem>
              );
            })}
          </OwcSelectDropdown>
        </div>


        <div style={{ flex: "1 1 220px", minWidth: 220, maxWidth: 320 }}>
          <OwcSelectDropdown
            label="Select group(s) *"
            name="groups"
            dataTestid="group-select-dropdown"
            style={{ width: "100%" }}
            multiple
            value={formik.values.groups || []}
            onSelectChange={(e) => {
              const selectedGroups = e.detail.map(
                (idx) => groupOptions[idx].value,
              );
              formik.setFieldValue("groups", selectedGroups);
              formik.setFieldTouched("groups", true);
            }}
            required
          >
            {groupOptions.map((option) => (
              <OwcListItem
                key={option.value}
                value={option.value}
                selected={
                  Array.isArray(formik.values.groups) &&
                  formik.values.groups.includes(option.value)
                }
              >
                <OwcCheckbox
                  checked={
                    Array.isArray(formik.values.groups) &&
                    formik.values.groups.includes(option.value)
                  }
                >
                  {option.label}
                </OwcCheckbox>
              </OwcListItem>
            ))}
          </OwcSelectDropdown>
        </div>
      </div>
    </ConfirmDialog>
  );
};

export default GroupDialog;
